/**
 * Holds SSRM row fetches for a table while its definition is out of date (e.g. another tab renamed a column).
 * Gating in the fetch covers AG Grid's own block loads, not only subscription-driven refreshes.
 * Released by {@link endDefinitionSync}, or after {@link MAX_HOLD_MS} if completion never arrives.
 */

const MAX_HOLD_MS = 10_000;

interface DefinitionSyncGate {
  promise: Promise<void>;
  release: () => void;
  timer: ReturnType<typeof setTimeout>;
}

const gates = new Map<string, DefinitionSyncGate>();

/** Opens a hold for `tableId`. Re-arming keeps the original deadline. */
export const beginDefinitionSync = (tableId: string): void => {
  if (!tableId || gates.has(tableId)) {
    return;
  }
  let release: () => void = () => {};
  const promise = new Promise<void>((resolve) => {
    release = resolve;
  });
  const timer = setTimeout(() => endDefinitionSync(tableId), MAX_HOLD_MS);
  gates.set(tableId, { promise, release, timer });
};

/** Releases waiters for `tableId`; no-op if none are open. */
export const endDefinitionSync = (tableId: string): void => {
  const gate = gates.get(tableId);
  if (!gate) {
    return;
  }
  gates.delete(tableId);
  clearTimeout(gate.timer);
  gate.release();
};

/** Resolves when the definition is settled, or immediately if no hold is open. */
export const awaitDefinitionSync = (tableId: string): Promise<void> => gates.get(tableId)?.promise ?? Promise.resolve();

/** True while a hold left open by a torn-down predecessor is still waiting to be closed. */
export const isDefinitionSyncHeld = (tableId: string): boolean => gates.has(tableId);
